import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import "./index.css";
import "./App.css";
import App from "./App.tsx";
import { AppProvider } from "./store/AppContext";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { ChunkReloadRestore } from "./components/ChunkReloadRestore";
import { BootLoader } from "./components/BootLoader";
import { BootErrorFallback } from "./components/BootErrorFallback";
import { initPerformanceTracking } from "./utils/performance";
import {
  performStorageCleanup,
  initializeStorageVersion,
} from "./services/storageCleanup.service";
import {
  cleanupServiceWorkers,
  initializeServiceWorkerVersion,
} from "./utils/serviceWorkerCleanup";
import {
  checkAndApplyServiceWorkerUpdate,
  setupPeriodicUpdateCheck,
  setupServiceWorkerMessageListener,
} from "./utils/serviceWorkerUpdate";
import { initSentry, captureException, setSentryRoute } from "./utils/sentry";
import * as Sentry from "@sentry/react";

// ── Demo path redirect ────────────────────────────────────────────────────
// /demo and /demo/pharmacy are router-owned canonical routes.
// React Router handles them — this rewrite must not touch them.
// Only rewrite generic shorthand sub-paths e.g. /demo/nutrition → /nutrition?demo=1
//
// MAINTENANCE: if you add a new named route under /demo/* in routes.tsx,
// add it to ROUTER_OWNED below so it is not accidentally rewritten.
const ROUTER_OWNED_DEMO_PATHS = ['/demo', '/demo/', '/demo/pharmacy', '/demo/pharmacy/'];
if (
  window.location.pathname.startsWith('/demo') &&
  !ROUTER_OWNED_DEMO_PATHS.includes(window.location.pathname)
) {
  const url = new URL(window.location.href);
  if (!url.searchParams.has('demo')) url.searchParams.set('demo', '1');
  // Preserve pharmacy partner context when rewriting /demo/pharmacy/* paths
  if (window.location.pathname.startsWith('/demo/pharmacy') && !url.searchParams.has('partner')) {
    url.searchParams.set('partner', 'pharmacy');
  }
  url.pathname = window.location.pathname.replace(/^\/demo\/pharmacy/, '').replace(/^\/demo/, '') || '/';
  window.location.replace(url.toString());
}
// ─────────────────────────────────────────────────────────────────────────

// Initialize Sentry BEFORE anything else
initSentry().catch((error) => {
  console.error("[Sentry] Failed to initialize:", error);
});

// Initialize performance tracking as early as possible
initPerformanceTracking();

// P0 Fix: Initialize storage version and perform cleanup before app renders
// This prevents blank screens caused by stale/corrupted storage
let storageCleanupComplete = false;
let storageCleanupPromise: Promise<void> | null = null;
let reloadInProgress = false; // Prevent reload loops

async function initializeApp(): Promise<void> {
  try {
    // Check if we're already in a reload cycle (prevent infinite loops)
    const reloadFlag = sessionStorage.getItem("coretrak.reload.inProgress");
    if (reloadFlag === "true") {
      storageCleanupComplete = true;
      return;
    }

    // Initialize storage version (first run)
    initializeStorageVersion();

    // BUG FIX v31: Initialize service worker version
    initializeServiceWorkerVersion();

    // BUG FIX v31: Clean up old service workers first (before storage cleanup)
    // This prevents old service workers from interfering with storage operations
    try {
      const swCleanupResult = await cleanupServiceWorkers();
      if (!swCleanupResult.cleaned) {
        // console.log("[App] Service worker cleanup performed:", swCleanupResult);
      }
    } catch (error) {
      console.error("[App] Service worker cleanup failed:", error);
    }

    // BUG FIX v17: Set up automatic service worker update checking
    // This ensures users get the latest build without hard refresh
    if (import.meta.env.PROD) {
      try {
        setupServiceWorkerMessageListener();
        setupPeriodicUpdateCheck();
        // BUG FIX v17: Check immediately (no delay) to detect updates as soon as possible
        // Use requestIdleCallback if available, otherwise check immediately
        if ("requestIdleCallback" in window) {
          requestIdleCallback(
            () => {
              checkAndApplyServiceWorkerUpdate();
            },
            { timeout: 1000 },
          );
        } else {
          // Fallback: check immediately but use setTimeout(0) to not block startup
          setTimeout(() => {
            checkAndApplyServiceWorkerUpdate();
          }, 0);
        }
      } catch (error) {
        console.error("[App] Failed to set up service worker updates:", error);
        // Continue anyway - app can work without auto-updates
      }
    }

    const cleanupResult = await performStorageCleanup();

    if (cleanupResult.cleaned || cleanupResult.migrated) {
      // Only reload if IndexedDB was reset (major cleanup)
      // For minor localStorage cleanup, we can continue without reload
      if (
        cleanupResult.migrated &&
        cleanupResult.errors.length === 0 &&
        !reloadInProgress
      ) {
        reloadInProgress = true;
        sessionStorage.setItem("coretrak.reload.inProgress", "true");

        // Small delay to ensure cleanup completes, then reload
        setTimeout(() => {
          // Clear the flag after a delay (in case reload fails)
          setTimeout(() => {
            sessionStorage.removeItem("coretrak.reload.inProgress");
          }, 2000);
          window.location.reload();
        }, 100);
        return;
      }
    }

    // Clear reload flag if cleanup completed without reload
    sessionStorage.removeItem("coretrak.reload.inProgress");
    storageCleanupComplete = true;
  } catch (error) {
    console.error("[App] Storage cleanup failed:", error);
    // Continue anyway - don't block app startup
    sessionStorage.removeItem("coretrak.reload.inProgress");
    storageCleanupComplete = true;
  }
}

// Demo mode: skip all storage cleanup — render immediately, no DB ops needed
const isDemoBootMode = (() => {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(window.location.hash.split('?')[1] || '');
    const sessionDemo = sessionStorage.getItem('coretrak.demo') === '1';
    return urlParams.get('demo') === '1' || hashParams.get('demo') === '1' || sessionDemo;
  } catch { return false; }
})();

// Start storage cleanup immediately (non-blocking) — skipped in demo mode
storageCleanupPromise = isDemoBootMode ? Promise.resolve() : initializeApp();

// Global error handlers to catch unhandled errors
window.addEventListener("error", (event) => {
  console.error("🚨 Global error caught:", event.error);
  console.error("Error details:", {
    message: event.message,
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno,
    error: event.error,
  });

  // Send to Sentry
  if (event.error) {
    captureException(event.error);
  } else {
    Sentry.captureMessage(event.message, {
      level: "error",
      tags: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    });
  }

  // Prevent default browser error handling
  // event.preventDefault();
});

window.addEventListener("unhandledrejection", (event) => {
  console.error("🚨 Unhandled promise rejection:", event.reason);
  console.error("Promise rejection details:", {
    reason: event.reason,
    promise: event.promise,
  });

  // Send to Sentry
  if (event.reason instanceof Error) {
    captureException(event.reason);
  } else {
    Sentry.captureMessage(
      `Unhandled promise rejection: ${String(event.reason)}`,
      {
        level: "error",
        tags: {
          type: "unhandled_rejection",
        },
      },
    );
  }

  // Prevent default browser error handling
  // event.preventDefault();
});

// Get root element with proper error handling
const rootElement = document.getElementById("root");

if (!rootElement) {
  // Critical: If root element doesn't exist, show error
  document.body.innerHTML = `
    <div style="display: flex; align-items: center; justify-content: center; min-height: 100vh; padding: 20px; text-align: center; font-family: system-ui, -apple-system, sans-serif;">
      <div style="max-width: 500px;">
        <h1 style="color: #dc2626; margin-bottom: 16px;">Application Error</h1>
        <p style="color: #6b7280; margin-bottom: 8px;">The application root element is missing.</p>
        <p style="color: #6b7280; font-size: 14px;">Please check the HTML file and ensure there is a &lt;div id="root"&gt;&lt;/div&gt; element.</p>
      </div>
    </div>
  `;
  throw new Error(
    'Root element not found. Make sure index.html has <div id="root"></div>',
  );
}

// Initialize Firebase with error handling
try {
  // Import Firebase config to ensure it initializes
  import("./services/firebase/firebase.config").catch((error) => {
    console.error("🚨 Failed to initialize Firebase:", error);
  });
} catch (error) {
  console.error("🚨 Critical: Failed to load Firebase configuration:", error);
}

// Render app with Error Boundary and Boot Loader
// Show BootLoader while storage cleanup is in progress
try {
  const root = createRoot(rootElement);

  // Render boot loader first (shows immediately)
  root.render(
    <StrictMode>
      <ErrorBoundary>
        <BootLoader
          timeout={3000}
          onTimeout={() => {
            // console.log(
            //   "[App] Boot timeout reached - showing refreshing message",
            // );
          }}
        />
      </ErrorBoundary>
    </StrictMode>,
  );

  // BUG FIX v32: Wait for storage cleanup to complete (max 3 seconds)
  // Reduced timeout to ensure app renders faster even if cleanup hangs
  // This ensures app always renders, even if cleanup fails or hangs
  const cleanupTimeout = setTimeout(() => {
    console.warn(
      "[App] Storage cleanup timeout (3s) - proceeding with app render",
    );
    if (!storageCleanupComplete && !reloadInProgress) {
      // console.log(
      //   "[App] Timeout reached - forcing app render despite incomplete cleanup",
      // );
      try {
        renderApp(root);
      } catch (error) {
        console.error("[App] Failed to render after timeout:", error);
        root.render(
          <BootErrorFallback
            error={
              error instanceof Error
                ? error
                : new Error("App initialization timeout")
            }
            onReload={() => window.location.reload()}
          />,
        );
      }
    }
  }, 3000); // Reduced from 5s to 3s for faster startup

  // Wait for storage cleanup, then render app
  storageCleanupPromise
    ?.then(() => {
      clearTimeout(cleanupTimeout);
      if (!reloadInProgress) {
        try {
          renderApp(root);
        } catch (error) {
          console.error("[App] Failed to render after cleanup:", error);
          root.render(
            <BootErrorFallback
              error={
                error instanceof Error ? error : new Error("App render failed")
              }
              onReload={() => window.location.reload()}
            />,
          );
        }
      }
      // If reload is in progress, don't render (page will reload)
    })
    .catch((error) => {
      clearTimeout(cleanupTimeout);
      console.error(
        "[App] Storage cleanup error, rendering app anyway:",
        error,
      );
      // BUG FIX v32: Always render app even if cleanup fails completely
      // This prevents blank screens when cleanup encounters critical errors
      if (!reloadInProgress) {
        try {
          renderApp(root);
        } catch (renderError) {
          console.error(
            "[App] Failed to render after cleanup error:",
            renderError,
          );
          root.render(
            <BootErrorFallback
              error={
                renderError instanceof Error
                  ? renderError
                  : new Error("App failed to start after cleanup error")
              }
              onReload={() => window.location.reload()}
            />,
          );
        }
      }
    });

  function renderApp(rootInstance: ReturnType<typeof createRoot>) {
    // Set initial route in Sentry
    setSentryRoute(window.location.pathname);

    try {
      rootInstance.render(
        <StrictMode>
          <ErrorBoundary>
            <BrowserRouter>
              <ChunkReloadRestore />
              <AppProvider>
                <App />
              </AppProvider>
            </BrowserRouter>
          </ErrorBoundary>
        </StrictMode>,
      );
    } catch (renderError) {
      // BUG FIX v32: Non-white-screen fallback if render fails
      console.error("[App] Critical: Failed to render app:", renderError);
      captureException(
        renderError instanceof Error
          ? renderError
          : new Error(String(renderError)),
      );
      rootInstance.render(
        <BootErrorFallback
          error={
            renderError instanceof Error
              ? renderError
              : new Error(String(renderError))
          }
          onReload={() => window.location.reload()}
        />,
      );
    }
  }
} catch (error) {
  // BUG FIX v32: Non-white-screen fallback if root creation fails
  console.error("🚨 Critical: Failed to create root or render app:", error);

  // Try to render using React if root was created
  if (rootElement) {
    try {
      const root = createRoot(rootElement);
      root.render(
        <BootErrorFallback
          error={error instanceof Error ? error : new Error(String(error))}
          onReload={() => window.location.reload()}
        />,
      );
    } catch {
      // Last resort: plain HTML fallback
      rootElement.innerHTML = `
        <div style="display: flex; align-items: center; justify-content: center; min-height: 100vh; padding: 20px; text-align: center; font-family: system-ui, -apple-system, sans-serif; background: linear-gradient(to bottom right, #f9fafb, #f3f4f6);">
          <div style="max-width: 500px; background: white; border-radius: 12px; padding: 32px; box-shadow: 0 10px 25px rgba(0,0,0,0.1);">
            <div style="color: #f59e0b; font-size: 48px; margin-bottom: 16px;">⚠️</div>
            <h1 style="color: #111827; margin-bottom: 16px; font-size: 24px; font-weight: 600;">App Failed to Start</h1>
            <p style="color: #6b7280; margin-bottom: 24px; font-size: 14px;">We encountered an issue while starting the app. Please try reloading.</p>
            <button 
              onclick="window.location.reload()" 
              style="background: #007c86; color: white; border: none; padding: 12px 24px; border-radius: 8px; cursor: pointer; font-size: 14px; font-weight: 500; width: 100%; transition: background 0.2s;"
              onmouseover="this.style.background='#006a73'"
              onmouseout="this.style.background='#007c86'"
            >
              Reload App
            </button>
            ${import.meta.env.DEV ? `<pre style="margin-top: 20px; text-align: left; background: #f3f4f6; padding: 16px; border-radius: 8px; overflow: auto; font-size: 12px; color: #374151;">${error instanceof Error ? error.stack : String(error)}</pre>` : ""}
          </div>
        </div>
      `;
    }
  } else {
    // Absolute last resort if root element doesn't exist
    document.body.innerHTML = `
      <div style="display: flex; align-items: center; justify-content: center; min-height: 100vh; padding: 20px; text-align: center; font-family: system-ui, -apple-system, sans-serif; background: linear-gradient(to bottom right, #f9fafb, #f3f4f6);">
        <div style="max-width: 500px; background: white; border-radius: 12px; padding: 32px; box-shadow: 0 10px 25px rgba(0,0,0,0.1);">
          <h1 style="color: #dc2626; margin-bottom: 16px;">Critical Error</h1>
          <p style="color: #6b7280; margin-bottom: 16px;">The application root element is missing. Please check the HTML file.</p>
          <button onclick="window.location.reload()" style="background: #dc2626; color: white; border: none; padding: 12px 24px; border-radius: 8px; cursor: pointer; font-size: 14px; font-weight: 500;">
            Reload Page
          </button>
        </div>
      </div>
    `;
  }
}
